import { z } from "zod";
import { CountryCode } from "../common/country.js";
import { Id } from "../common/ids.js";
import { Locale } from "../common/localized-text.js";
import { Money } from "../common/money.js";
import { Quantity } from "../common/quantity.js";
import { Timestamp } from "../common/time.js";
import { defineRoute } from "../http/route.js";
import { Availability, ListingKind } from "./listing.js";
/**
 * What the public site reads (WEB-1, card 039), without authentication, through the `public_read` views only. Text
 * comes in the requested locale, falling back to English; images are published renditions (ADR 0018).
 */
const Public = { surface: "public", auth: "none", permission: "none", marks: [] };
const Slug = z.string().min(1).max(120);
const LocaleQuery = z.object({ locale: Locale.default("en") });
export const PublicImage = z
    .object({
    alt: z.string().nullable(),
    renditions: z
        .array(z.object({ width: z.number().int().positive(), url: z.url() }))
        .min(1)
        .describe("WebP copies, narrowest first; a URL never changes what it serves"),
})
    .meta({ id: "PublicImage" });
export const PriceRange = z
    .object({ low: Money, high: Money })
    .meta({ id: "PriceRange", description: "The lowest and highest unit prices across SKUs and tiers" });
export const PublicShopSummary = z
    .object({ slug: z.string(), name: z.string(), country: CountryCode, logoUrl: z.url().nullable() })
    .meta({ id: "PublicShopSummary" });
export const PublicAttribute = z
    .object({
    key: z.string(),
    label: z.string(),
    value: z
        .union([z.string(), z.number(), z.boolean(), z.array(z.string())])
        .describe("A choice's value is its label in the locale"),
    unit: z.string().nullable(),
})
    .meta({ id: "PublicAttribute" });
const CategoryCrumb = z.object({ slug: z.string(), name: z.string() });
export const PublicProduct = z
    .object({
    id: Id.describe("For Contact seller (card 046), and Inquire with trade"),
    slug: z.string(),
    locale: Locale,
    locales: z.array(Locale).describe("The languages the seller wrote the product in"),
    listingKind: ListingKind,
    name: z.string(),
    description: z.string().nullable(),
    categoryPath: z.array(CategoryCrumb).describe("From the top of the tree to the product's leaf"),
    attributes: z.array(PublicAttribute),
    hsCode: z.string().nullable(),
    originCountry: CountryCode.nullable(),
    serviceRegions: z.array(CountryCode).nullable(),
    leadTimeDays: z.number().int().nullable(),
    availability: Availability,
    availableQuantity: Quantity.nullable(),
    inquiryOnly: z.boolean(),
    sample: Money.nullable(),
    skus: z.array(z.object({
        id: Id.describe("Named in an inquiry's line when the product has several options"),
        code: z.string(),
        options: z.record(z.string(), z.string()),
        moq: Quantity,
        unit: z.string(),
        tiers: z
            .array(z.object({ minQuantity: Quantity, unitPrice: Money }))
            .describe("Empty when inquiry-only"),
    })),
    priceRange: PriceRange.nullable().describe("Null when inquiry-only"),
    images: z.array(PublicImage),
    shop: PublicShopSummary,
    publishedAt: Timestamp,
    updatedAt: Timestamp,
    rating: z
        .object({ count: z.number().int().positive(), average: z.number() })
        .nullable()
        .describe("The product's average rating in published reviews, from the first one (card 092)"),
})
    .meta({ id: "PublicProduct" });
export const PublicProductCard = z
    .object({
    slug: z.string(),
    name: z.string(),
    listingKind: ListingKind,
    availability: Availability,
    inquiryOnly: z.boolean(),
    priceRange: PriceRange.nullable(),
    image: PublicImage.nullable(),
    publishedAt: Timestamp,
})
    .meta({ id: "PublicProductCard" });
export const PublicShop = z
    .object({
    slug: z.string(),
    locale: Locale,
    locales: z.array(Locale),
    name: z.string(),
    description: z.string().nullable(),
    country: CountryCode,
    serviceRegions: z.array(CountryCode),
    qualifications: z.array(z.string()).describe("Titles only; the documents stay private"),
    logoUrl: z.url().nullable(),
    openedAt: Timestamp,
    updatedAt: Timestamp,
    rating: z
        .object({
        count: z.number().int().positive(),
        quality: z.number(),
        communication: z.number(),
        delivery: z.number(),
    })
        .nullable()
        .describe("Averages of the published reviews, from the first one (card 092)"),
})
    .meta({ id: "PublicShop" });
export const getPublicProduct = defineRoute({
    ...Public,
    id: "catalog.public.products.get",
    method: "GET",
    path: "/v1/public/products/{slug}",
    summary: "A live product of a shop that sells, with what its page and structured data show",
    prd: ["WEB-1", "CAT-3"],
    request: { params: z.object({ slug: Slug }), query: LocaleQuery },
    responses: { 200: { description: "The product", schema: PublicProduct } },
    errors: ["NOT_FOUND", "RATE_LIMITED", "VALIDATION_FAILED"],
});
export const getPublicShop = defineRoute({
    ...Public,
    id: "catalog.public.shops.get",
    method: "GET",
    path: "/v1/public/shops/{slug}",
    summary: "A shop that sells, with its profile and qualification titles",
    prd: ["WEB-1", "CAT-1"],
    request: { params: z.object({ slug: Slug }), query: LocaleQuery },
    responses: { 200: { description: "The shop", schema: PublicShop } },
    errors: ["NOT_FOUND", "RATE_LIMITED", "VALIDATION_FAILED"],
});
export const listPublicShopProducts = defineRoute({
    ...Public,
    id: "catalog.public.shops.products",
    method: "GET",
    path: "/v1/public/shops/{slug}/products",
    summary: "A shop's live products, most recently published first",
    prd: ["WEB-1"],
    request: {
        params: z.object({ slug: Slug }),
        query: LocaleQuery.extend({
            limit: z.coerce.number().int().min(1).max(60).default(24),
            cursor: z.string().min(1).max(200).optional(),
        }),
    },
    responses: {
        200: {
            description: "Products",
            schema: z.object({ items: z.array(PublicProductCard), nextCursor: z.string().nullable() }),
        },
    },
    errors: ["NOT_FOUND", "RATE_LIMITED", "VALIDATION_FAILED"],
});
export const listSitemapEntries = defineRoute({
    ...Public,
    id: "catalog.public.sitemap",
    method: "GET",
    path: "/v1/public/sitemap/{kind}",
    summary: "Every public product or shop by slug, with its last change, for the sitemap; every locale has a page",
    prd: ["WEB-1"],
    request: {
        params: z.object({ kind: z.enum(["products", "shops"]) }),
        query: z.object({
            limit: z.coerce.number().int().min(1).max(5000).default(1000),
            cursor: z.string().min(1).max(200).optional(),
        }),
    },
    responses: {
        200: {
            description: "Entries",
            schema: z.object({
                items: z.array(z.object({ slug: z.string(), lastModified: Timestamp })),
                nextCursor: z.string().nullable(),
            }),
        },
    },
    errors: ["RATE_LIMITED", "VALIDATION_FAILED"],
});
export const publicCatalogRoutes = [
    getPublicProduct,
    getPublicShop,
    listPublicShopProducts,
    listSitemapEntries,
];
//# sourceMappingURL=public.routes.js.map